export interface LocalCustomerCartItem {
  productId: string;
  quantity: number;
}

export interface LocalCustomerCart {
  shopId: string;
  items: LocalCustomerCartItem[];
}

const LOCAL_CART_KEY = "efasol_customer_cart";
const LOCAL_CART_CHANGE_EVENT = "efasol_customer_cart_changed";

function canUseStorage() {
  return (
    typeof window !== "undefined" && typeof window.localStorage !== "undefined"
  );
}

let cachedRaw: string | null = null;
let cachedCart: LocalCustomerCart | null = null;

export function getLocalCustomerCart(): LocalCustomerCart | null {
  if (!canUseStorage()) return null;

  const raw = window.localStorage.getItem(LOCAL_CART_KEY);

  // Return the cached reference if the raw string hasn't changed.
  // useSyncExternalStore uses Object.is to compare snapshots — returning a
  // new object on every call would cause an infinite re-render loop.
  if (raw === cachedRaw) return cachedCart;

  cachedRaw = raw;

  if (!raw) {
    cachedCart = null;
    return null;
  }

  try {
    cachedCart = JSON.parse(raw) as LocalCustomerCart;
    return cachedCart;
  } catch {
    window.localStorage.removeItem(LOCAL_CART_KEY);
    cachedRaw = null;
    cachedCart = null;
    return null;
  }
}


export function setLocalCustomerCart(cart: LocalCustomerCart | null) {
  if (!canUseStorage()) return;

  if (!cart || cart.items.length === 0) {
    window.localStorage.removeItem(LOCAL_CART_KEY);
    notifyLocalCustomerCartChange();
    return;
  }

  const productIds = new Set(cart.items.map((item) => item.productId));
  if (productIds.size !== cart.items.length) {
    throw new Error("Cart items must have unique product ids.");
  }

  window.localStorage.setItem(LOCAL_CART_KEY, JSON.stringify(cart));
  notifyLocalCustomerCartChange();
}

export function clearLocalCustomerCart() {
  if (!canUseStorage()) return;
  window.localStorage.removeItem(LOCAL_CART_KEY);
  notifyLocalCustomerCartChange();
}

export function subscribeLocalCustomerCart(callback: () => void) {
  if (!canUseStorage()) return () => {};

  const handleStorage = (event: StorageEvent) => {
    if (event.key === LOCAL_CART_KEY) {
      callback();
    }
  };

  window.addEventListener("storage", handleStorage);
  window.addEventListener(LOCAL_CART_CHANGE_EVENT, callback);

  return () => {
    window.removeEventListener("storage", handleStorage);
    window.removeEventListener(LOCAL_CART_CHANGE_EVENT, callback);
  };
}

function notifyLocalCustomerCartChange() {
  if (!canUseStorage()) return;
  window.dispatchEvent(new Event(LOCAL_CART_CHANGE_EVENT));
}

export function addLocalCustomerCartItem(params: {
  shopId: string;
  productId: string;
  quantity: number;
}) {
  const current = getLocalCustomerCart();
  const existingShopId = current?.shopId;

  if (existingShopId && existingShopId !== params.shopId) {
    throw new Error(
      "Your cart can only contain items from one shop at a time. Check your cart",
    );
  }

  const items = [...(current?.items ?? [])];

  const existingItem = items.find(
    (item) => item.productId === params.productId,
  );

  if (existingItem) {
    existingItem.quantity += params.quantity;
  } else {
    items.push({
      productId: params.productId,
      quantity: params.quantity,
    });
  }

  setLocalCustomerCart({
    shopId: params.shopId,
    items,
  });
}

export function updateLocalCustomerCartItem(
  productId: string,
  quantity: number,
) {
  const current = getLocalCustomerCart();
  if (!current) return;

  const nextItems = current.items
    .map((item) =>
      item.productId === productId ? { ...item, quantity } : item,
    )
    .filter((item) => item.quantity > 0);

  setLocalCustomerCart({
    shopId: current.shopId,
    items: nextItems,
  });
}

export function removeLocalCustomerCartItem(productId: string) {
  const current = getLocalCustomerCart();
  if (!current) return;

  const nextItems = current.items.filter(
    (item) => item.productId !== productId,
  );
  setLocalCustomerCart({
    shopId: current.shopId,
    items: nextItems,
  });
}
